import generateVideoPageViewData from "../../utils/tracking/generateVideoPageViewData";
import React, { Component } from "react";
import * as PropTypes from "prop-types";
import BelowTheFold from "./BelowTheFold";
import VideoMetadata from "../video-metadata/VideoMetadata";

import VideoPlayer from "../video-player/VideoPlayer";
import { PersonalizedList } from "../personalized-list/PersonalizedList";
import LiveMarquee from "../live-marquee/LiveMarquee";
import Tabs from "../tabs/Tabs";
import Pane from "../tabs/Pane";
import FadeInOut from "../../utils/FadeInOut";
import { parselyTracker } from "../../utils/tracking/ParselyTracker";
import LiveSchedule from "../live-schedule/LiveSchedule";
import StreamsDropdown from "../streams-dropdown/StreamsDropdown";
import GTMTracker from "../../utils/tracking/GTMTracker";
import { PAGEVIEW_TRACKING } from "../../constants/Tracking";
import { fullScheduleComponentProps } from "../../constants/ComponentProps";
import Transcript from "../transcript/Transcript";
import { BUSINESS_SITE } from "../../constants/Sites";
import PlugScript from "../plug/PlugScript";

const EVENT_PREFIX = "video";
const RAIL_FADE_OUT_CSS_CLASS = "video-article__rail--fade-out";

const BOUND_METHODS = [
    "_onTranscriptOn",
    "_onTranscriptOff",
    "_onTranscriptReady",
    "_onVideoChange",
    "_onTimeUpdate",
    "_onSelectVideo",
    "_onPlaylistSelected",
    "_onPlaylistContinuousPlay",
];

interface State {
    isTranscriptOn: boolean;
    isTranscriptReady: boolean;
    playerTime: number;
    transcriptRecords?: any;
}

type Props = PropTypes.InferPropsInner<typeof Video.propTypes>;

export default class Video extends Component<Props, State> {
    continuousPlayCount: number;

    _fadeInOut: any;

    _railEl: any;

    constructor(props) {
        super(props);
        this.continuousPlayCount = 0;
        this.state = defaultTranscriptState();

        BOUND_METHODS.forEach(
            (methodName) => (this[methodName] = this[methodName].bind(this))
        );
    }

    render() {
        const { props } = this;
        const {
            videoStory,
            channels: { liveTV: channels, byChannelId: channelsByChannelId },
            isBelowTheFoldEnabled,
            relatedSeriesAndVideosData,
            recircShowModules,
            site,
            videoEmbedServiceEnv,
            enableSkylight,
            cojpPath,
        } = props;
        const isBusinessSite = site === BUSINESS_SITE;
        const belowTheFoldProps = {
            relatedSeriesAndVideosData,
            recircShowModules,
        };

        const { isLandingPageVideo } = videoStory;

        const continuousPlayPreviousUrl = props.isContinuousPlay
            ? props.referrer
            : "";
        const playerOptions = {
            videoEmbedServiceEnv,
            bmmrId: videoStory.video.bmmrId,
            playerId: "video-article__player",
            canonicalUrl: videoStory.url,
            autoplay: true,
            continuousPlayPreviousUrl,
            videoList: props.videoList,
            onPlaylistSelected: this._onPlaylistSelected,
            onPlaylistContinuousPlay: this._onPlaylistContinuousPlay,
            eventPrefix: EVENT_PREFIX,
            site: props.site,
            onTranscriptOn: this._onTranscriptOn,
            onTranscriptOff: this._onTranscriptOff,
            isTranscriptOn: this.state.isTranscriptOn,
            onTranscriptReady: this._onTranscriptReady,
            onVideoChange: this._onVideoChange,
            onTimeUpdate: this._onTimeUpdate,
            videoStory,
            enableSkylight,
        };
        const personalizedListOptions = {
            shouldShowVideoList: props.shouldShowVideoList,
            videoList: props.videoList,
            currentVideoBmmrId: videoStory.video.bmmrId,
            onSelectVideo: this._onSelectVideo,
            onLoadUserRecommendation: props.onLoadUserRecommendation,
            onPlaylistEndReached: props.onPlaylistEndReached,
            eventPrefix: EVENT_PREFIX,
            localizer: props.localizer,
            isLandingPageVideo,
        };
        const metadataOptions = {
            publishedAt: videoStory.publishedAt,
            formattedPublishedAt: videoStory.formattedPublishedAt,
            headlineHtml: videoStory.headline.html,
            summaryHtml: videoStory.summary.html,
            linkToSeriesPageEnabled: props.linkToSeriesPageEnabled,
            series: videoStory.series,
        };
        const tabOptions = {
            selectedIndex: props.selectedTabIndex,
            onSelectedIndexChange: props.changeSelectedTab,
            eventPrefix: EVENT_PREFIX,
        };
        const { currentChannelId } = props.schedule;
        const streamsDropdownOptions = {
            currentChannelId,
            title: props.localizer ? props.localizer("STREAM_SCHEDULE") : "",
            eventPrefix: EVENT_PREFIX,
            localizer: props.localizer,
            channels,
            channelsByChannelId,
            cojpPath,
        };
        const showFullScheduleLink =
            isBusinessSite &&
            !channelsByChannelId[currentChannelId ?? ""].isEventChannel;
        const liveScheduleOptions = {
            currentChannelId,
            channelsByChannelId,
            isFetching: props.schedule.isFetching,
            schedules: props.schedule.schedules,
            eventPrefix: EVENT_PREFIX,
            isActiveChannel: false,
            showFullScheduleLink,
            localizer: props.localizer,
        };
        const liveMarqueeOptions = {
            liveChannelId: props.liveNow.liveChannelId,
            showTitle: props.liveNow.showTitle,
            guests: props.liveNow.guests,
            eventPrefix: EVENT_PREFIX,
            localizer: props.localizer,
        };
        const transcriptOptions = {
            title: "CC-Transcript",
            onOpen: this._onTranscriptOn,
            onClose: this._onTranscriptOff,
            records: this.state.transcriptRecords || [
                {
                    begin: 0,
                    text: videoStory.video.transcript, //inserts placeholder transcript text into the html on page load for SEO
                },
            ],
            isTranscriptOn: this.state.isTranscriptOn,
            isTranscriptReady: this.state.isTranscriptReady,
            playerTime: this.state.playerTime,
        };

        return (
            <div className="video-article__container">
                <article className="video-article">
                    <div className="video-article__player">
                        <VideoPlayer {...playerOptions} />
                        <LiveMarquee {...liveMarqueeOptions} />
                    </div>
                    <aside
                        className="video-article__rail"
                        ref={(element) => (this._railEl = element)}
                    >
                        <Transcript {...transcriptOptions} />
                        <Tabs {...tabOptions}>
                            <Pane
                                paneId="for_you"
                                label={
                                    props.localizer
                                        ? props.localizer("FOR_YOU")
                                        : ""
                                }
                            >
                                <PersonalizedList
                                    {...personalizedListOptions}
                                />
                            </Pane>
                            <Pane
                                paneId="live_tv"
                                label={
                                    props.localizer
                                        ? props.localizer("LIVE_TV")
                                        : ""
                                }
                            >
                                <StreamsDropdown {...streamsDropdownOptions} />
                                <LiveSchedule {...liveScheduleOptions} />
                            </Pane>
                        </Tabs>
                    </aside>
                    <div className="video-article__below-player">
                        <div className="video-article__content">
                            <div className="video-article__content-main">
                                <VideoMetadata {...metadataOptions} />
                                <hr className="video-article__below-the-folder-separator" />
                                {isBelowTheFoldEnabled && (
                                    <BelowTheFold {...belowTheFoldProps} />
                                )}
                            </div>
                            <div className="video-article__content-rail"></div>
                        </div>
                    </div>
                </article>
                <PlugScript assetHost={this.props.assetHost} />
            </div>
        );
    }

    componentDidMount() {
        this._startFadeInOutBehavior();
        if (this.props.updateLiveBar) {
            this.props.updateLiveBar();
        }
    }

    componentWillUnmount() {
        this._stopFadeInOutBehavior();
    }

    componentDidUpdate(prevProps) {
        const { videoStory, isContinuousPlay, failedVideoBmmrId } = this.props;

        if (failedVideoBmmrId) {
            const nextBmmrId = this._getNextVideoBmmrId(failedVideoBmmrId);
            if (nextBmmrId && this.props.onPlaylistContinuousPlay) {
                this.props.onPlaylistContinuousPlay(nextBmmrId);
            }
            return;
        }

        if (prevProps && videoStory.id !== prevProps.videoStory.id) {
            if (!isContinuousPlay) {
                this._personalizationTracking();
            }
            this._parselyTracking();
            this._pageViewTracking();
        }
    }

    _startFadeInOutBehavior() {
        if (this._fadeInOut) {
            return;
        }

        this._fadeInOut = new FadeInOut(this._railEl, RAIL_FADE_OUT_CSS_CLASS);
        this._fadeInOut.initialize();
    }

    _stopFadeInOutBehavior() {
        if (!this._fadeInOut) {
            return;
        }

        this._fadeInOut.dispose();
        this._fadeInOut = null;
    }

    _onTranscriptOn() {
        this._stopFadeInOutBehavior();
        this.setState({
            isTranscriptOn: true,
        });
    }

    _onTranscriptOff() {
        this._startFadeInOutBehavior();
        this.setState({
            isTranscriptOn: false,
        });
    }

    _onTranscriptReady(transcriptRecords) {
        this.setState({
            isTranscriptReady: true,
            transcriptRecords,
        });
    }

    _onTimeUpdate(playerTime) {
        if (this.state.isTranscriptOn) {
            this.setState({
                playerTime,
            });
        }
    }

    _onVideoChange() {
        this.setState(defaultTranscriptState());
    }

    _personalizationTracking = () => {
        const {
            videoStory: {
                site: siteId,
                video: { bmmrId: resourceId },
                resourceType,
            },
            personalizationTrackingEnabled,
        } = this.props;
        if (!personalizationTrackingEnabled) {
            return;
        }

        GTMTracker.trackPersonalization({
            siteId,
            resourceId,
            resourceType,
            referrer: this._getReferrer(),
        });
    };

    _parselyTracking() {
        parselyTracker({
            url: this._getWindowLocation(),
            referrer: this._getReferrer(),
        });
    }

    _pageViewTracking() {
        const { videoStory, site } = this.props;
        const pageViewData = generateVideoPageViewData(videoStory, {
            site: site ?? undefined,
        });
        GTMTracker.push({ ...pageViewData, event: PAGEVIEW_TRACKING });
    }

    _getReferrer() {
        return this._getDocumentReferrer() || this.props.referrer;
    }

    _getWindowLocation() {
        return window.location.href;
    }

    _getDocumentReferrer() {
        return document.referrer;
    }

    _getNextVideoBmmrId(failedBmmrId) {
        const videoList = this.props.videoList;
        if (!videoList) {
            return null;
        }
        for (let i = 0; i < videoList.length; ++i) {
            const video = videoList[i].video;
            if (video.bmmrId === failedBmmrId && i + 1 < videoList.length) {
                return videoList[i + 1].video.bmmrId;
            }
        }
    }

    _onSelectVideo(bmmrId, event) {
        event.preventDefault();
        this._resetContinuousPlayCount();
        if (this.props.onSelectVideo) {
            this.props.onSelectVideo(bmmrId);
        }
    }

    _onPlaylistSelected(bmmrId) {
        this._resetContinuousPlayCount();
        if (this.props.onPlaylistSelected) {
            this.props.onPlaylistSelected(bmmrId);
        }
    }

    _onPlaylistContinuousPlay(bmmrId) {
        this.continuousPlayCount++;
        GTMTracker.push({ moduleIndex: this.continuousPlayCount });
        if (this.props.onPlaylistContinuousPlay) {
            this.props.onPlaylistContinuousPlay(bmmrId);
        }
    }

    _resetContinuousPlayCount() {
        GTMTracker.push({ moduleIndex: undefined });
        this.continuousPlayCount = 0;
    }

    static defaultProps = {
        selectedTabIndex: 0,
        referrer: "",
        isContinuousPlay: false,
        updateLiveBar: () => {},
        failedVideoBmmrId: null,
        localizer: (input) => input,
        personalizationTrackingEnabled: false,
        linkToSeriesPageEnabled: false,
        isBelowTheFoldEnabled: false,
        videoEmbedServiceEnv: "",
        enableSkylight: false,
    };

    static propTypes = {
        breakingNews: PropTypes.object,
        videoStory: PropTypes.any.isRequired,
        selectedTabIndex: PropTypes.number,
        videoList: PropTypes.arrayOf(PropTypes.any),
        recircShowModules: PropTypes.arrayOf(PropTypes.any),
        shouldShowVideoList: PropTypes.bool,
        personalizationTrackingEnabled: PropTypes.bool,
        schedule: PropTypes.shape({
            currentChannelId: PropTypes.string,
            isFetching: PropTypes.bool,
            schedules: fullScheduleComponentProps,
        }).isRequired,
        referrer: PropTypes.string,
        isContinuousPlay: PropTypes.bool,
        onSelectVideo: PropTypes.func,
        onLoadUserRecommendation: PropTypes.func,
        changeSelectedTab: PropTypes.func,
        onPlaylistSelected: PropTypes.func,
        onPlaylistContinuousPlay: PropTypes.func,
        updateLiveBar: PropTypes.func,
        failedVideoBmmrId: PropTypes.string,
        onPlaylistEndReached: PropTypes.func,
        localizer: PropTypes.func,
        linkToSeriesPageEnabled: PropTypes.bool,
        site: PropTypes.string,
        isBelowTheFoldEnabled: PropTypes.bool,
        relatedSeriesAndVideosData: PropTypes.arrayOf(PropTypes.object),
        channels: PropTypes.any,
        liveNow: PropTypes.any,
        videoEmbedServiceEnv: PropTypes.string,
        assetHost: PropTypes.string,
        enableSkylight: PropTypes.bool,
        cojpPath: PropTypes.string,
    };
}

function defaultTranscriptState() {
    return {
        isTranscriptReady: false,
        isTranscriptOn: false,
        playerTime: 0,
    };
}
